import type {
  AppResource,
  AssetType,
  BulkCeiling,
  BulkOperationKind,
  BulkPreflightResource,
  BulkRowInput,
  Target,
} from '@studio/shared';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { assetTypeLabel, targetKey, targetLabel } from '../app/history.js';
import { api, ApiError } from '../shell/api.js';
import { Failure } from '../shell/EmptyState.js';
import { useAsync } from '../shell/useAsync.js';
import { expandMatrix, money, rangeLine, rowKey } from './matrix.js';

const ASSET_TYPES: readonly AssetType[] = ['appIcon', 'featureGraphic', 'screenshotSet', 'previewVideo'];

/** A row as this panel holds it, whether it came from the matrix or was named outright. */
interface WantedRow {
  readonly appId: string;
  readonly target: Target;
  readonly assetType: AssetType;
  readonly experimentId: string | null;
}

/** One row that names the Experiment it works on, rather than a cell of a matrix. */
export interface PinnedRow {
  readonly appId: string;
  readonly target: Target;
  readonly assetType: AssetType;
  readonly experimentId: string;
}

export interface PreflightSeed {
  readonly operationKind: BulkOperationKind;
  readonly appIds: readonly string[];
  /** Empty means each App is tested where it already says it is tested. */
  readonly targets: readonly Target[];
  readonly assetTypes: readonly AssetType[];
  readonly sharedBrief: string;
  readonly exceptions: Readonly<Record<string, string>>;
  readonly sourceExperimentId: string | null;
  readonly sourceCandidateId: string | null;
  /**
   * `exportPreparation` does not expand anything. It packs Experiments that already
   * exist and whose Play Test Pair a person already chose, so its rows are named one by
   * one and there is no Apps x Targets x Asset Types to tick.
   */
  readonly pinnedRows?: readonly PinnedRow[];
}

export function seedFrom(resource: BulkPreflightResource): PreflightSeed {
  return {
    operationKind: resource.operationKind,
    appIds: [...new Set(resource.rows.map((row) => row.appId))],
    targets: [...new Map(resource.rows.map((row) => [targetKey(row.target), row.target])).values()],
    assetTypes: [...new Set(resource.rows.map((row) => row.assetType))],
    sharedBrief: resource.sharedBrief,
    exceptions: Object.fromEntries(
      resource.rows
        .filter((row) => row.rowException !== null)
        .map((row) => [rowKey(row), row.rowException as string]),
    ),
    sourceExperimentId: resource.sourceExperimentId,
    sourceCandidateId: resource.sourceCandidateId,
    ...(resource.operationKind === 'exportPreparation'
      ? {
          pinnedRows: resource.rows
            .filter((row) => row.experimentId !== null)
            .map((row) => ({
              appId: row.appId,
              target: row.target,
              assetType: row.assetType,
              experimentId: row.experimentId as string,
            })),
        }
      : {}),
  };
}

/**
 * The matrix before anything is bought.
 *
 * Every row is visible and every row can be removed, because the number on the button
 * is the number of Experiments this will create and that number is the operator's to
 * decide. The estimate is a range with the expensive rows named: one number would read
 * as a promise, and a range with an unmarked video row in it would read as a lie.
 *
 * Nothing here says when the run will finish.
 */
export function Preflight({
  apps,
  seed,
  onLaunched,
}: {
  apps: readonly AppResource[];
  seed: PreflightSeed;
  onLaunched: (runId: string) => void;
}): ReactNode {
  const [appIds, setAppIds] = useState<readonly string[]>(seed.appIds);
  const [targets, setTargets] = useState<readonly Target[]>(seed.targets);
  const [market, setMarket] = useState('');
  const [locale, setLocale] = useState('');
  const [assetTypes, setAssetTypes] = useState<readonly AssetType[]>(seed.assetTypes);
  const [sharedBrief, setSharedBrief] = useState(seed.sharedBrief);
  const [exceptions, setExceptions] = useState<Readonly<Record<string, string>>>(seed.exceptions);
  const [removed, setRemoved] = useState<readonly string[]>([]);
  const [ceilingMode, setCeilingMode] = useState<'default' | 'amount' | 'noLimit'>('default');
  const [ceilingAmount, setCeilingAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const pinned = seed.pinnedRows ?? null;
  const packing = pinned !== null;

  /**
   * How a row is identified while this panel is open.
   *
   * A matrix cell is its coordinates: the operator ticked an App, a Target and an Asset
   * Type, and the Experiment a previous run created out of that cell is not part of what
   * they ticked. Packing is the one mode where the Experiment IS the choice, and two of
   * an App's Experiments can share all three coordinates - so it joins the key there and
   * only there.
   */
  const keyOf = useCallback(
    (row: { readonly appId: string; readonly target: Target; readonly assetType: AssetType; readonly experimentId?: string | null }): string =>
      packing ? `${rowKey(row)}|${row.experimentId ?? ''}` : rowKey(row),
    [packing],
  );

  const wanted: readonly WantedRow[] = useMemo(() => {
    const rows: readonly WantedRow[] =
      pinned === null
        ? expandMatrix(apps, appIds, targets, assetTypes).map((row) => ({ ...row, experimentId: null }))
        : pinned.map((row) => ({ ...row }));
    return rows.filter((row) => !removed.includes(keyOf(row)));
  }, [pinned, apps, appIds, targets, assetTypes, removed, keyOf]);
  // The matrix is priced from its rows. The direction and the row exceptions change
  // what is asked for, never what it costs, so typing does not re-price anything.
  const signature = wanted.map(keyOf).join(',');

  const loaded = useAsync<BulkPreflightResource | null>(async () => {
    if (wanted.length === 0) return null;
    return api.bulkPreflight({
      operationKind: seed.operationKind,
      ...(seed.sourceExperimentId === null ? {} : { sourceExperimentId: seed.sourceExperimentId }),
      ...(seed.sourceCandidateId === null ? {} : { sourceCandidateId: seed.sourceCandidateId }),
      rows: wanted.map((row) => ({
        appId: row.appId,
        target: row.target,
        assetType: row.assetType,
        ...(row.experimentId === null ? {} : { experimentId: row.experimentId }),
      })),
    });
  }, [signature, seed.operationKind, seed.sourceExperimentId, seed.sourceCandidateId]);

  const launch = useCallback(async () => {
    const preflight = loaded.value;
    if (preflight === null) return;
    setBusy(true);
    setFailed(null);
    try {
      const rows: BulkRowInput[] = preflight.rows.map((row) => {
        const exception = exceptions[keyOf(row)] ?? '';
        return {
          appId: row.appId,
          target: row.target,
          assetType: row.assetType,
          ...(row.experimentId === null ? {} : { experimentId: row.experimentId }),
          ...(exception === '' ? {} : { rowException: exception }),
        };
      });
      const ceiling: BulkCeiling | undefined =
        ceilingMode === 'noLimit'
          ? { kind: 'noLimit' }
          : ceilingMode === 'amount' && Number.isFinite(Number(ceilingAmount)) && ceilingAmount !== ''
            ? { kind: 'amount', usd: Number(ceilingAmount) }
            : undefined;
      const detail = await api.launchBulkRun({
        operationKind: seed.operationKind,
        sharedBrief,
        ...(seed.sourceExperimentId === null ? {} : { sourceExperimentId: seed.sourceExperimentId }),
        ...(seed.sourceCandidateId === null ? {} : { sourceCandidateId: seed.sourceCandidateId }),
        rows,
        ...(ceiling === undefined ? {} : { ceiling }),
      });
      onLaunched(detail.run.id);
    } catch (caught) {
      setFailed(caught instanceof ApiError ? caught.message : 'Не удалось запустить Bulk Run.');
    } finally {
      setBusy(false);
    }
  }, [ceilingAmount, ceilingMode, exceptions, keyOf, loaded.value, onLaunched, seed, sharedBrief]);

  const toggle = <T,>(list: readonly T[], value: T): readonly T[] =>
    list.includes(value) ? list.filter((entry) => entry !== value) : [...list, value];

  const preflight = loaded.value;

  return (
    <section className="panel" aria-label="Preflight">
      <p className="eyebrow">
        {packing ? 'Подготовить exports · временная группа выполнения' : 'Создать много · временная группа выполнения'}
      </p>
      <h2>{packing ? 'Посмотрите, что будет упаковано. Потом пусть идёт.' : 'Посмотрите матрицу. Потом пусть идёт.'}</h2>
      <p className="note">
        {packing
          ? 'Каждая строка упаковывает уже существующий Experiment, используя Play Test Pair, который кто-то выбрал внутри него. Здесь Experiment не создаётся и ничего не генерируется.'
          : 'Apps × Targets × Asset Types разворачивается в независимые Experiments. Каждый использует свой App Profile и Listing Snapshot; файлы другого App не переносятся.'}
      </p>

      <fieldset hidden={packing}>
        <legend>Apps</legend>
        {apps.map((entry) => (
          <label key={entry.id}>
            <input
              type="checkbox"
              checked={appIds.includes(entry.id)}
              onChange={() => setAppIds((current) => toggle(current, entry.id))}
            />
            {entry.displayName}
          </label>
        ))}
      </fieldset>

      <fieldset hidden={packing}>
        <legend>Targets</legend>
        <p className="note">
          Оставьте пустым — и каждый App тестируется там, где его Profile уже говорит, что его тестируют.
        </p>
        <ul aria-label="Выбранные Targets">
          {targets.map((target) => (
            <li key={targetKey(target)}>
              {targetLabel(target)}
              <button
                type="button"
                className="ghost"
                onClick={() => setTargets((current) => current.filter((entry) => targetKey(entry) !== targetKey(target)))}
              >
                {`Удалить ${targetLabel(target)}`}
              </button>
            </li>
          ))}
        </ul>
        <label htmlFor="target-market">Market</label>
        <input id="target-market" value={market} placeholder="BR" onChange={(event) => setMarket(event.target.value)} />
        <label htmlFor="target-locale">Locale</label>
        <input id="target-locale" value={locale} placeholder="pt-BR" onChange={(event) => setLocale(event.target.value)} />
        <button
          type="button"
          className="ghost"
          disabled={market.trim().length < 2 || locale.trim().length < 2}
          onClick={() => {
            const added: Target = { kind: 'market', market: market.trim(), locale: locale.trim() };
            setTargets((current) =>
              current.some((entry) => targetKey(entry) === targetKey(added)) ? current : [...current, added],
            );
            setMarket('');
            setLocale('');
          }}
        >
          Добавить market
        </button>
        <button
          type="button"
          className="ghost"
          onClick={() =>
            setTargets((current) => (current.some((entry) => entry.kind === 'global') ? current : [...current, { kind: 'global' }]))
          }
        >
          Добавить Global
        </button>
      </fieldset>

      <fieldset hidden={packing}>
        <legend>Asset Types</legend>
        {ASSET_TYPES.map((assetType) => (
          <label key={assetType}>
            <input
              type="checkbox"
              checked={assetTypes.includes(assetType)}
              onChange={() => setAssetTypes((current) => toggle(current, assetType))}
            />
            {assetTypeLabel(assetType)}
          </label>
        ))}
      </fieldset>

      {packing ? null : (
        <>
          <label htmlFor="shared-direction">Общее направление</label>
          <textarea
            id="shared-direction"
            value={sharedBrief}
            rows={3}
            placeholder="Сделайте свободу ощутимой сразу."
            onChange={(event) => setSharedBrief(event.target.value)}
          />
        </>
      )}

      {loaded.error === null ? null : <Failure message={loaded.error} onRetry={loaded.reload} />}

      {preflight === null && loaded.error === null ? (
        <p className="note">
          {packing
            ? 'Выберите хотя бы один Experiment для упаковки.'
            : 'Выберите хотя бы один App и один Asset Type, чтобы увидеть матрицу.'}
        </p>
      ) : preflight === null ? null : (
        <>
          <table aria-label="Matrix">
            <thead>
              <tr>
                <th scope="col">App</th>
                <th scope="col">Target</th>
                <th scope="col">Asset</th>
                <th scope="col">Короткое исключение</th>
                <th scope="col">Оценка</th>
                <th scope="col">Нельзя запустить</th>
                <th scope="col"> </th>
              </tr>
            </thead>
            <tbody>
              {preflight.rows.map((row) => (
                <tr key={row.identityKey}>
                  <td>{row.appName}</td>
                  <td>{targetLabel(row.target)}</td>
                  <td>
                    {assetTypeLabel(row.assetType)}
                    {row.expensive ? <span className="chip"> дорого</span> : null}
                  </td>
                  <td>
                    <input
                      aria-label={`Короткое исключение · ${row.appName} ${assetTypeLabel(row.assetType)}`}
                      value={exceptions[keyOf(row)] ?? ''}
                      placeholder="Необязательно"
                      disabled={packing}
                      onChange={(event) =>
                        setExceptions((current) => ({ ...current, [keyOf(row)]: event.target.value }))
                      }
                    />
                  </td>
                  <td>{rangeLine(row.estimateLowUsd, row.estimateHighUsd)}</td>
                  {/*
                    A refused row is shown rather than dropped: the launch is refused by
                    the whole matrix, so the operator has to be able to see which row it
                    was and take it out.
                  */}
                  <td>{row.refusal === null ? '' : row.refusal}</td>
                  <td>
                    <button
                      type="button"
                      className="ghost"
                      onClick={() => setRemoved((current) => [...current, keyOf(row)])}
                    >
                      {`Исключить ${row.appName} ${assetTypeLabel(row.assetType)}`}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <p>{packing ? `Experiments упаковано: ${preflight.experimentCount}` : `Experiments: ${preflight.experimentCount}`}</p>
          <p>{`Дорогих строк: ${preflight.expensiveRowCount}`}</p>
          <p>{`Диапазон оценки: ${rangeLine(preflight.estimateLowUsd, preflight.estimateHighUsd)}`}</p>
          {!packing ? null : (
            <p className="note">
              Упаковка ничего не покупает. Эти строки не создают Experiment: каждая использует названный в ней.
            </p>
          )}

          <fieldset hidden={packing}>
            <legend>Потолок стоимости</legend>
            <label>
              <input
                type="radio"
                name="ceiling"
                checked={ceilingMode === 'default'}
                onChange={() => setCeilingMode('default')}
              />
              {`Взять верхнюю оценку (${money(preflight.defaultCeilingUsd)})`}
            </label>
            <label>
              <input
                type="radio"
                name="ceiling"
                checked={ceilingMode === 'amount'}
                onChange={() => setCeilingMode('amount')}
              />
              Задать сумму
            </label>
            <input
              aria-label="Потолок стоимости в долларах"
              value={ceilingAmount}
              inputMode="decimal"
              disabled={ceilingMode !== 'amount'}
              onChange={(event) => setCeilingAmount(event.target.value)}
            />
            <label>
              <input
                type="radio"
                name="ceiling"
                checked={ceilingMode === 'noLimit'}
                onChange={() => setCeilingMode('noLimit')}
              />
              Без лимита
            </label>
          </fieldset>
          {packing ? null : (
            <p className="note">
              Завершённая работа сохраняется, если достигнут потолок, а строки, которые ещё не стартовали, ждут
              допуска, а не падают с ошибкой.
            </p>
          )}

          {failed === null ? null : (
            <p className="failure" role="alert">
              {failed}
            </p>
          )}
          <button
            className="primary"
            type="button"
            disabled={busy || preflight.rows.length === 0}
            onClick={() => void launch()}
          >
            {busy
              ? 'Запускаю…'
              : packing
                ? `Упаковать ${preflight.experimentCount} Experiments`
                : `Запустить ${preflight.experimentCount} Experiments`}
          </button>
        </>
      )}
    </section>
  );
}
